import type { MouseEvent } from 'react'
import { AppEventTypes, outcomeFailureProps } from '@buxus/shared'

import { trackEvent } from './webAnalytics'

/** County–state pairs from reverse geocode. */
export type MapLocationCountyState = { county: string; state: string }

export function formatLocationCountyState(
  pairs: readonly MapLocationCountyState[] | null | undefined,
): string | undefined {
  if (!pairs?.length) return undefined
  return pairs.map((p) => `${p.county}, ${p.state}`).join(' · ')
}

export function buildPipelineSegmentPayload(
  feature: { id?: string | number; properties?: Record<string, unknown> | null } | null | undefined,
  index: number,
  count: number,
): Record<string, string | number> {
  const props = (feature?.properties ?? {}) as Record<string, unknown>
  const out: Record<string, string | number> = {
    segmentIndex: index,
    segmentNumber: index + 1,
    segmentCount: count,
  }
  if (feature?.id !== undefined && feature?.id !== null) {
    out.segmentFeatureId = String(feature.id)
  }
  const operatorName = props.CO_NAME ?? props.CLIENTNAME
  if (typeof operatorName === 'string' && operatorName.trim()) {
    out.pipelineOperatorName = operatorName.trim()
  }
  const operatorId = props.ZOHO_OPERA
  if (typeof operatorId === 'string' && operatorId.trim()) {
    out.pipelineCompanyId = operatorId.trim()
  }
  return out
}

/** Shared screen context for in-app interaction events (mobile parity). */
export type ScreenInteractionContext = {
  screen: string
  operator?: string
  asset?: string
  /** Product sub-type display name (e.g. "Natural Gas") — mobile `hazard` on hazard screens. */
  hazard?: string
  /** Facility storage label on hazard screens and file-open events. */
  storageLocationName?: string
}

export function trackAccordionToggle(
  expanded: boolean,
  title: string,
  ctx: ScreenInteractionContext,
): void {
  trackEvent(
    expanded ? AppEventTypes.ACCORDION_EXPANDED : AppEventTypes.ACCORDION_COLLAPSED,
    { ...ctx, title },
  )
}

export function trackUrlPressed(
  payload: ScreenInteractionContext & { url: string; title?: string },
): void {
  trackEvent(AppEventTypes.URL_PRESSED, payload)
}

export function trackLinkPressed(
  payload: ScreenInteractionContext & {
    linkTarget: string
    title?: string
    navigationReason?: string
    latitude?: string
    longitude?: string
  },
): void {
  trackEvent(AppEventTypes.LINK_PRESSED, payload)
}

export function trackPhonePressed(
  payload: ScreenInteractionContext & { phone: string; contact?: string },
): void {
  trackEvent(AppEventTypes.PHONE_PRESSED, payload)
}

export function trackEmailPressed(
  payload: ScreenInteractionContext & { email: string; contact?: string },
): void {
  trackEvent(AppEventTypes.EMAIL_PRESSED, payload)
}

export function trackButtonPressed(
  payload: ScreenInteractionContext & {
    button: string
    request_type?: string
    component?: string
    mapRole?: string
    latitude?: string
    longitude?: string
  },
): void {
  trackEvent(AppEventTypes.BUTTON_PRESSED, payload)
}

export function trackFormSubmitted(
  payload: ScreenInteractionContext & { request_type: string },
): void {
  trackEvent(AppEventTypes.FORM_SUBMITTED, payload)
}

export type MapAddressSearchResult =
  | 'success'
  | 'not_found'
  | 'failed'
  | 'validation_failed'

/** Map address search form submitted (geocode attempted; does not log destination). */
export function trackMapAddressSearchSubmitted(
  payload: ScreenInteractionContext & {
    mapRole?: string
    result: MapAddressSearchResult
    queryLength?: number
  },
): void {
  trackEvent(AppEventTypes.FORM_SUBMITTED, {
    ...payload,
    form: 'map_address_search',
  })
}

export function trackMenuItemSelected(
  payload: {
    component: string
    menu: string
    selection: string
    navigationSource?: 'sidebar' | 'breadcrumb' | 'content_child_nav'
  } & Record<string, unknown>,
): void {
  trackEvent(AppEventTypes.MENU_ITEM_SELECTED, payload)
}

export function trackFileViewed(
  payload: ScreenInteractionContext & { fileId?: string; title?: string },
): void {
  trackEvent(AppEventTypes.FILE_VIEWED, payload)
}

export type MapTapResult =
  | 'pipeline_shown'
  | 'jurisdiction_denied'
  | 'no_feature'
  | 'dismissed'

export function trackMapTapped(
  payload: ScreenInteractionContext & {
    result: MapTapResult
    mapRole?: string
    denyReason?: string
    /** Total segments at tap site; stable for the popup session (pagination uses map_popup_displayed). */
    segmentCount?: number
    latitude?: string
    longitude?: string
    locationCountyState?: string
  },
): void {
  trackEvent(AppEventTypes.MAP_TAPPED, payload)
}

export type MapPopupDisplayTrigger = 'map_tap' | 'pagination'
export type MapPopupPaginationDirection = 'previous' | 'next'

/** Pipeline (or future) map popup segment became visible. */
export function trackMapPopupDisplayed(
  payload: ScreenInteractionContext & {
    popupType: 'pipeline'
    trigger: MapPopupDisplayTrigger
    paginationDirection?: MapPopupPaginationDirection
    mapRole?: string
    latitude?: string
    longitude?: string
    locationCountyState?: string
    feature:
      | { id?: string | number; properties?: Record<string, unknown> | null }
      | null
      | undefined
    segmentIndex: number
    segmentCount: number
  },
): void {
  const { feature, segmentIndex, segmentCount, trigger, paginationDirection, ...ctx } = payload
  trackEvent(AppEventTypes.MAP_POPUP_DISPLAYED, {
    ...ctx,
    trigger,
    ...(paginationDirection ? { paginationDirection } : {}),
    ...buildPipelineSegmentPayload(feature, segmentIndex, segmentCount),
  })
}

export function trackMapLoadFailed(
  payload: ScreenInteractionContext & {
    mapRole?: string
    message?: string
    cause?: string
  },
): void {
  trackEvent(AppEventTypes.MAP_LOAD_FAILED, {
    ...payload,
    ...outcomeFailureProps({
      errorMessage: payload.message ?? 'Map load failed',
      failureKind: payload.cause === 'offline_or_dns' ? 'connectivity' : 'unknown',
    }),
  })
}

export function trackNavigationRedirected(
  payload: ScreenInteractionContext & {
    reason: string
    fromScreen: string
    toScreen: string
    fromScreenConstant?: string
    toScreenConstant?: string
  },
): void {
  trackEvent(AppEventTypes.NAVIGATION_REDIRECTED, payload)
}

/** Delegate clicks on `<a href>` inside `.rich` HTML blocks. */
export function trackRichHtmlLinkClick(
  event: MouseEvent<HTMLElement>,
  ctx: ScreenInteractionContext,
): void {
  const anchor = (event.target as HTMLElement | null)?.closest?.('a[href]')
  if (!anchor || !(anchor instanceof HTMLAnchorElement)) return

  const href = anchor.getAttribute('href')?.trim()
  if (!href) return

  const title = anchor.textContent?.trim() || undefined

  if (/^mailto:/i.test(href)) {
    trackEmailPressed({
      ...ctx,
      email: href.replace(/^mailto:/i, ''),
      contact: title,
    })
    return
  }

  if (/^tel:/i.test(href)) {
    trackPhonePressed({
      ...ctx,
      phone: href.replace(/^tel:/i, ''),
      contact: title,
    })
    return
  }

  if (/^https?:\/\//i.test(href) || href.startsWith('//')) {
    trackUrlPressed({ ...ctx, url: href, title })
  }
}
